import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './ExameDetalhes.css';

const styles = {
  badgeAtivo: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '16px',
    fontSize: '0.85rem',
    fontWeight: '600',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeBloqueado: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '16px',
    fontSize: '0.85rem',
    fontWeight: '600',
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#333',
    margin: '0 0 16px 0',
    paddingBottom: '8px',
    borderBottom: '2px solid #1976d2',
    display: 'inline-block',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: '#999',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: '500',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#666',
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #eee',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
    fontSize: '1rem',
  },
  errorState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#c62828',
    fontSize: '1rem',
  },
  searchInput: {
    padding: '8px 14px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.85rem',
    outline: 'none',
    width: '250px',
  },
  countersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  counterCard: (color) => ({
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color}`,
  }),
  counterLabel: {
    fontSize: '0.75rem',
    color: '#666',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  counterValue: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#333',
  },
};

function ConvenioDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [convenio, setConvenio] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [tabelaPrecos, setTabelaPrecos] = useState({ tabela: null, itens: [], total: 0, tabelasDisponiveis: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchPrecos, setSearchPrecos] = useState('');

  useEffect(() => {
    fetchConvenio();
  }, [id]);

  const fetchConvenio = async () => {
    setLoading(true);
    setError('');
    try {
      const [convResp, planosResp, precosResp] = await Promise.all([
        axios.get(`${API_URL}/convenios/${id}`),
        axios.get(`${API_URL}/convenios/${id}/planos`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/convenios/${id}/tabela-precos`).catch(() => ({ data: { tabela: null, itens: [], total: 0, tabelasDisponiveis: [] } })),
      ]);
      setConvenio(convResp.data);
      setPlanos(Array.isArray(planosResp.data) ? planosResp.data : (planosResp.data?.data || []));
      setTabelaPrecos(precosResp.data || { tabela: null, itens: [], total: 0, tabelasDisponiveis: [] });
    } catch (err) {
      setError(err.response?.status === 404 ? 'Convenio nao encontrado.' : 'Erro ao carregar convenio: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCNPJ = (value) => {
    if (!value) return '-';
    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length === 14) {
      return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  if (loading) {
    return <div style={styles.loadingState}>Carregando convenio...</div>;
  }

  if (error) {
    return (
      <div className="data-page" style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh', fontFamily: 'Inter, Roboto, "Open Sans", sans-serif' }}>
        <button className="back-button" onClick={() => navigate('/convenios')}>← Voltar</button>
        <div style={styles.errorState}>{error}</div>
      </div>
    );
  }

  if (!convenio) return null;

  const precosComValor = tabelaPrecos.itens.filter(i => i.VALOR > 0);
  const totalValor = tabelaPrecos.itens.reduce((acc, i) => acc + (i.VALOR || 0), 0);

  const filteredPrecos = tabelaPrecos.itens.filter(item => {
    if (!searchPrecos) return true;
    const term = searchPrecos.toLowerCase();
    return (
      (item.EXAME_ID && item.EXAME_ID.toLowerCase().includes(term)) ||
      (item.EXAME_DESCRICAO && item.EXAME_DESCRICAO.toLowerCase().includes(term)) ||
      (item.CODIGO_PROCEDIMENTO && item.CODIGO_PROCEDIMENTO.toLowerCase().includes(term))
    );
  });

  return (
    <div className="data-page" style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh', fontFamily: 'Inter, Roboto, "Open Sans", sans-serif' }}>
      {/* Header */}
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/convenios')} className="back-button">← Voltar</button>
        <h1>{convenio.RAZAOSOCIAL || convenio.IDENTIFICACAO}</h1>
        <span style={convenio.BLOQUEADO === 'T' ? styles.badgeBloqueado : styles.badgeAtivo}>
          {convenio.BLOQUEADO === 'T' ? 'Bloqueado' : 'Ativo'}
        </span>
        <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
          Codigo: <strong>{convenio.IDENTIFICACAO}</strong>
          {convenio.NOME_FANTASIA && <span> — {convenio.NOME_FANTASIA}</span>}
        </p>
      </div>

      {/* Resumo */}
      <div className="responsive-grid" style={styles.countersRow}>
        <div style={styles.counterCard('#1976d2')}>
          <div style={styles.counterLabel}>Planos</div>
          <div style={styles.counterValue}>{planos.length}</div>
        </div>
        <div style={styles.counterCard('#7b1fa2')}>
          <div style={styles.counterLabel}>Exames na Tabela</div>
          <div style={{ ...styles.counterValue, color: '#7b1fa2' }}>{tabelaPrecos.total}</div>
        </div>
        <div style={styles.counterCard('#2e7d32')}>
          <div style={styles.counterLabel}>Com Valor</div>
          <div style={{ ...styles.counterValue, color: '#2e7d32' }}>{precosComValor.length}</div>
        </div>
        <div style={styles.counterCard('#e65100')}>
          <div style={styles.counterLabel}>Sem Valor</div>
          <div style={{ ...styles.counterValue, color: '#e65100' }}>{tabelaPrecos.total - precosComValor.length}</div>
        </div>
      </div>

      {/* Dados Cadastrais */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Dados Cadastrais</h3>
        <div className="responsive-grid" style={styles.detailGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Razao Social</span>
            <span style={styles.detailValue}>{convenio.RAZAOSOCIAL || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Nome Fantasia</span>
            <span style={styles.detailValue}>{convenio.NOME_FANTASIA || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>CNPJ</span>
            <span style={{ ...styles.detailValue, fontFamily: 'monospace' }}>{formatCNPJ(convenio.CGC)}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Tipo</span>
            <span style={styles.detailValue}>{convenio.TIPO || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Registro ANS</span>
            <span style={styles.detailValue}>{convenio.REG_ANS || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Tabela de Precos</span>
            <span style={styles.detailValue}>
              {tabelaPrecos.tabela || convenio.ID_TABELA_PRECO_PAINEL || '-'}
              {tabelaPrecos.tabelasDisponiveis && tabelaPrecos.tabelasDisponiveis.length > 0 && (
                <span style={{ color: '#666', fontSize: '0.85rem' }}> ({tabelaPrecos.tabelasDisponiveis.map(t => t.PLANO_NOME).join(', ')})</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Endereco + Contato lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Endereco</h3>
          <div style={{ ...styles.detailGrid, gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
              <span style={styles.detailLabel}>Endereco</span>
              <span style={styles.detailValue}>{convenio.ENDERECO || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Bairro</span>
              <span style={styles.detailValue}>{convenio.BAIRRO || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Cidade</span>
              <span style={styles.detailValue}>{convenio.CIDADE || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Estado</span>
              <span style={styles.detailValue}>{convenio.ESTADO || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>CEP</span>
              <span style={styles.detailValue}>{convenio.CEP || '-'}</span>
            </div>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Contato</h3>
          <div style={{ ...styles.detailGrid, gridTemplateColumns: '1fr 1fr' }}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Telefone</span>
              <span style={styles.detailValue}>{convenio.TELEFONE || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Email</span>
              <span style={{ ...styles.detailValue, wordBreak: 'break-all' }}>{convenio.EMAIL || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Contato</span>
              <span style={styles.detailValue}>{convenio.CONTATO || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Responsavel</span>
              <span style={styles.detailValue}>{convenio.RESPONSAVEL || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Observacao */}
      {convenio.OBSERVACAO && (
        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Observacao</h3>
          <p style={{
            fontSize: '0.9rem', color: '#333', backgroundColor: '#fafafa',
            padding: '12px 16px', borderRadius: '6px', border: '1px solid #eee',
            margin: 0, lineHeight: '1.5', whiteSpace: 'pre-wrap',
          }}>
            {convenio.OBSERVACAO}
          </p>
        </div>
      )}

      {/* Planos */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Planos Vinculados</h3>
        {planos.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: '#999', margin: 0 }}>Nenhum plano vinculado.</p>
        ) : (
          <div style={{ borderRadius: '6px', border: '1px solid #eee', overflow: 'hidden', overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '80px' }}>ID</th>
                  <th style={styles.th}>Descricao</th>
                  <th style={{ ...styles.th, width: '80px' }}>Tabela</th>
                  <th style={styles.th}>Tipo Atend.</th>
                  <th style={{ ...styles.th, width: '100px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {planos.map((plano, idx) => (
                  <tr key={plano.ID || idx} style={idx % 2 === 0 ? {} : { backgroundColor: '#fafafa' }}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{plano.ID || '-'}</td>
                    <td style={styles.td}>{plano.DESCRICAO || '-'}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace' }}>{plano.TABELA || '-'}</td>
                    <td style={styles.td}>{plano.TIPO_ATEND || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={plano.BLOQUEADO === 'T' ? styles.badgeBloqueado : styles.badgeAtivo}>
                        {plano.BLOQUEADO === 'T' ? 'Bloqueado' : 'Ativo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tabela de Precos */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Tabela de Precos</h3>
        {!tabelaPrecos.tabela && (!tabelaPrecos.tabelasDisponiveis || tabelaPrecos.tabelasDisponiveis.length === 0) ? (
          <p style={{ fontSize: '0.85rem', color: '#999', margin: 0 }}>Nenhuma tabela de precos vinculada.</p>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                Tabela <strong>{tabelaPrecos.tabela}</strong> — {tabelaPrecos.total} exames cadastrados
                {searchPrecos && ` (${filteredPrecos.length} encontrados)`}
              </p>
              <input
                type="text"
                placeholder="Buscar exame na tabela..."
                value={searchPrecos}
                onChange={(e) => setSearchPrecos(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <div style={{ borderRadius: '6px', border: '1px solid #eee', overflow: 'hidden', maxHeight: '600px', overflowY: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: '90px', position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1 }}>Codigo</th>
                    <th style={{ ...styles.th, position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1 }}>Exame</th>
                    <th style={{ ...styles.th, width: '130px', position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1 }}>Cod. Procedimento</th>
                    <th style={{ ...styles.th, width: '120px', position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1 }}>Material</th>
                    <th style={{ ...styles.th, width: '100px', textAlign: 'right', position: 'sticky', top: 0, backgroundColor: '#fafafa', zIndex: 1 }}>Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPrecos.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>Nenhum exame encontrado.</td>
                    </tr>
                  ) : (
                    filteredPrecos.map((item, idx) => (
                      <tr
                        key={item.EXAME_ID || idx}
                        style={{
                          ...(idx % 2 === 0 ? {} : { backgroundColor: '#fafafa' }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/laudos/${item.EXAME_ID}`)}
                      >
                        <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>{item.EXAME_ID}</td>
                        <td style={styles.td}>{item.EXAME_DESCRICAO || '-'}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.CODIGO_PROCEDIMENTO || '-'}</td>
                        <td style={{ ...styles.td, fontSize: '0.8rem' }}>{item.MATERIAL_NOME || '-'}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: item.VALOR > 0 ? '#2e7d32' : '#999' }}>
                          {typeof item.VALOR === 'number' ? item.VALOR.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {filteredPrecos.length > 0 && (
                  <tfoot>
                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: '700' }}>
                      <td colSpan="4" style={{ ...styles.td, textAlign: 'right', borderTop: '2px solid #ddd' }}>
                        Total ({filteredPrecos.length} exames)
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', borderTop: '2px solid #ddd', color: '#1976d2', fontSize: '0.95rem' }}>
                        R$ {filteredPrecos.reduce((acc, i) => acc + (i.VALOR || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConvenioDetalhes;
